import { ZodObject, ZodOptional, type ZodRawShape, type ZodTypeAny } from 'zod'

import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration, NextStepConfig } from '@flows/core/types/stepTypes'

export type ElementFragment<
  TElements extends readonly AnyStepContentElement[] = readonly AnyStepContentElement[],
> = {
  elements: TElements
  schema: ZodObject<ZodRawShape>
}

type NextStepFields<TNext> = TNext extends string ? { nextStep: TNext } : { nextStepConfig: TNext }

const unwrapOptional = (schema: ZodTypeAny | undefined) =>
  schema instanceof ZodOptional ? schema.unwrap() : schema

const mergeSchemas = (
  base: ZodObject<ZodRawShape>,
  added: ZodObject<ZodRawShape>
): ZodObject<ZodRawShape> =>
  base.extend(
    Object.fromEntries(
      Object.entries(added.shape).map(([key, schema]) => {
        const existing = base.shape[key]
        const [existingObject, addedObject] = [existing, schema].map(unwrapOptional)
        if (!(existingObject instanceof ZodObject && addedObject instanceof ZodObject)) {
          return [key, schema]
        }
        const merged = mergeSchemas(existingObject, addedObject)
        return [key, existing?.isOptional() && schema.isOptional() ? merged.optional() : merged]
      })
    )
  )

export const combineFragments = (
  first: ElementFragment,
  ...rest: readonly ElementFragment[]
): ElementFragment => ({
  elements: [first, ...rest].flatMap(({ elements }) => elements),
  schema: rest.reduce((combined, { schema }) => mergeSchemas(combined, schema), first.schema),
})

export const fragmentStep = <
  const TElements extends readonly AnyStepContentElement[],
  const TNext extends string | NextStepConfig<string>,
>(
  { elements, schema }: ElementFragment<TElements>,
  params: StepConfigParams,
  next: TNext
) => ({
  ...({ schema, stepContent: elements, subFlow: params.subFlow } satisfies AnyStepConfiguration),
  ...((typeof next === 'string'
    ? { nextStep: next }
    : { nextStepConfig: next }) as NextStepFields<TNext>),
})
